import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocs, updateDoc, collection, query, where, doc, limit } from 'firebase/firestore';
import { db } from '@/firebase';
import { useNavigate } from 'react-router-dom';
import { PawPrint, Check } from 'lucide-react';

interface Pet {
  id: string;
  name: string;
}

interface Task {
  id: string;
  petId: string;
  petName: string;
  careType: string;
  completed: boolean;
}

export const PetsWidget: React.FC<{ householdId: string | null }> = ({ householdId }) => {
  const { t } = useTranslation();
  const [pets, setPets] = useState<Pet[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!householdId) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const petsQuery = query(
          collection(db, 'households', householdId, 'pets'),
          limit(10)
        );
        const petsSnapshot = await getDocs(petsQuery);

        const petsData: Pet[] = petsSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name as string,
        }));
        setPets(petsData);

        const allTasks: Task[] = [];
        for (const pet of petsData) {
          const tasksQuery = query(
            collection(db, 'households', householdId, 'pets', pet.id, 'careTasks'),
            where('completed', '==', false)
          );
          const tasksSnapshot = await getDocs(tasksQuery);
          tasksSnapshot.docs.forEach((taskDoc) => {
            const taskData = taskDoc.data();
            allTasks.push({
              id: taskDoc.id,
              petId: pet.id,
              petName: pet.name,
              careType: taskData.careType as string,
              completed: taskData.completed as boolean,
            });
          });
        }
        setTasks(allTasks);
      } catch (error) {
        console.error('Error fetching pets data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [householdId]);

  const handleCompleteTask = async (task: Task) => {
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    try {
      if (!householdId) return;
      await updateDoc(
        doc(db, 'households', householdId, 'pets', task.petId, 'careTasks', task.id),
        { completed: true }
      );
    } catch (error) {
      console.error('Error completing task:', error);
      setTasks((prev) => [...prev, task]);
    }
  };

  if (!householdId) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 text-slate-900 dark:text-white border border-slate-100 dark:border-slate-800">
      <div className="flex items-center gap-2 mb-4">
        <PawPrint className="w-4 h-4 text-emerald-400" />
        <h2 className="text-base font-semibold">{t('pets.widget_title', 'Pet Care')}</h2>
        {pets.length > 0 && (
          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full">
            {pets.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-3/4"></div>
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-1/2"></div>
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-5/6"></div>
        </div>
      ) : pets.length === 0 ? (
        <div className="text-slate-600 dark:text-slate-300">
          <p className="text-sm">{t('pets.widget_empty', 'No pets registered')}</p>
          <button onClick={() => navigate('/pets')} className="text-emerald-400 text-xs mt-2 hover:underline">
            {t('pets.widget_add', 'Add pets')} →
          </button>
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-emerald-400 text-sm">
          {t('pets.widget_all_cared', '{{count}} pet(s) · all cared for ✓', { count: pets.length })}
        </p>
      ) : (
        <div className="space-y-2">
          {tasks.slice(0, 3).map((task) => (
            <div key={task.id} className="flex items-center justify-between bg-slate-100/80 dark:bg-slate-700/50 p-2 rounded">
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {task.careType} — <span className="text-slate-900 dark:text-white">{task.petName}</span>
              </span>
              <button
                onClick={() => handleCompleteTask(task)}
                className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 flex items-center justify-center transition-colors"
                aria-label="Mark task complete"
              >
                <Check className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pets.length > 0 && (
        <button onClick={() => navigate('/pets')} className="text-emerald-400 text-xs mt-4 hover:underline">
          {t('pets.widget_view_all', 'View all')} →
        </button>
      )}
    </div>
  );
};